import { useEffect, useRef, useState } from "react";
import { Search, X, Play, Loader2, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TABS = ["Trending", "Music", "Movies", "Comedy", "Sports", "Gaming", "News", "Education"];

function formatViews(n) {
  if (!n) return "";
  if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, "") + "B views";
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M views";
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K views";
  return n + " views";
}

function VideoCard({ v, onPick }) {
  return (
    <button
      data-testid="search-result-card"
      onClick={() => onPick(v.videoId)}
      className="group text-left rounded-xl overflow-hidden bg-white/[0.03] border border-white/5 hover:border-[#00D9FF]/60 hover:bg-white/[0.06] transition-colors"
    >
      <div className="relative aspect-video bg-[#0F1636] overflow-hidden">
        {v.thumbnail && <img src={v.thumbnail} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />}
        {v.duration && (
          <span className="absolute bottom-1.5 right-1.5 bg-black/85 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">{v.duration}</span>
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
          <span className="w-10 h-10 rounded-full bg-[#00D9FF] flex items-center justify-center"><Play className="w-4 h-4 text-white ml-0.5" /></span>
        </div>
      </div>
      <div className="p-2.5">
        <p className="text-sm font-semibold text-[#F1F1F5] leading-snug line-clamp-2">{v.title}</p>
        <p className="text-xs text-[#A0A0AB] mt-1 truncate">{v.channel}</p>
        {v.views ? <p className="text-[11px] text-[#71717A] mt-0.5">{formatViews(v.views)}</p> : null}
      </div>
    </button>
  );
}

export default function SearchPanel({ open, onClose, onPick, region }) {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("Trending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const reqRef = useRef(0);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 250);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    const myReq = ++reqRef.current;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = q
          ? `${BACKEND_URL}/api/youtube/search?q=${encodeURIComponent(q)}&region=${region || "US"}`
          : `${BACKEND_URL}/api/youtube/trending?region=${region || "US"}${tab !== "Trending" ? `&category=${tab}` : ""}`;
        const r = await fetch(url);
        const d = await r.json();
        if (myReq !== reqRef.current) return;
        if (!r.ok) { setError(d.detail || "Something went wrong"); setItems([]); }
        else setItems(d.items || []);
      } catch (e) {
        if (myReq === reqRef.current) setError("Couldn't reach YouTube. Try again.");
      } finally {
        if (myReq === reqRef.current) setLoading(false);
      }
    };
    const t = setTimeout(run, q ? 500 : 0); // debounce typing 500ms
    return () => clearTimeout(t);
  }, [open, query, tab, region]);

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />
      <div
        data-testid="search-panel"
        className={`fixed top-0 left-0 bottom-0 z-50 w-full max-w-[460px] bg-[#0A0A12] border-r border-white/10 flex flex-col shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="p-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 bg-[#141c40] border border-white/10 rounded-full px-4 h-11 focus-within:border-[#00D9FF] transition-colors">
              <Search className="w-4 h-4 text-[#00D9FF] shrink-0" />
              <input
                ref={inputRef}
                data-testid="youtube-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for videos to watch..."
                className="flex-1 bg-transparent outline-none text-sm text-[#F1F1F5] placeholder:text-[#71717A]"
              />
              {query && (
                <button data-testid="search-clear" onClick={() => setQuery("")} className="text-[#71717A] hover:text-white"><X className="w-4 h-4" /></button>
              )}
            </div>
            <button data-testid="search-panel-close" onClick={onClose} className="w-11 h-11 rounded-full bg-white/5 border border-white/10 text-[#A0A0AB] hover:text-white flex items-center justify-center shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
          {!query && (
            <div className="flex gap-1.5 mt-3 overflow-x-auto chat-scroll pb-1" data-testid="search-categories">
              {TABS.map((t) => (
                <button
                  key={t}
                  data-testid={`search-tab-${t}`}
                  onClick={() => setTab(t)}
                  className={`shrink-0 flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${tab === t ? "bg-[#00D9FF] text-navy-ink" : "bg-white/5 text-[#A0A0AB] hover:bg-white/10"}`}
                >
                  {t === "Trending" && <TrendingUp className="w-3 h-3" />}{t}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto chat-scroll p-4">
          {loading && (
            <div className="flex items-center justify-center py-16 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>
          )}
          {!loading && error && (
            <p data-testid="search-error" className="text-center text-sm text-[#00D9FF] py-12 px-6">{error}</p>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="text-center text-sm text-[#71717A] py-12">No videos found.</p>
          )}
          {!loading && !error && items.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="search-results">
              {items.map((v) => <VideoCard key={v.videoId} v={v} onPick={onPick} />)}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
